import { GraphQLClientProvider } from '@dixa/lib-integrations-partner-widgets';
import {
  Avatar,
  Badge,
  Box,
  EmptyState,
  FlexBox,
  InputSelectionList,
  Page,
  SearchInput,
  SelectionList,
  Spinner,
} from '@dixa/lib-shared-habla';
import { config } from 'lib-shared-backend-config';
import { useMemo, useState } from 'react';

import { Integration } from '../../utils/useIntegrations/integrations';
import { useIntegrations } from '../../utils/useIntegrations/useIntegrations';

import { IntegrationsCard } from './card/integrationsCard';
import styles from './integrationsOverview.module.scss';
import { useCustomCards } from './utils/useCustomCards/useCustomCards';
import { usePartnerWidgets } from './utils/usePartnerWidgets/usePartnerWidgets';

export const IntegrationsOverview = (): JSX.Element => {
  const [typeFilter, setTypeFilter] = useState<Integration[] | undefined>();
  const [searchTerm, setSearchTerm] = useState<string | undefined>();

  const { isLoading: isWidgetsLoading, partnerWidgets } = usePartnerWidgets();
  const { isLoading: isCustomCardsLoading, customCards } = useCustomCards();
  const integrations = useIntegrations();

  const isLoading = isWidgetsLoading || isCustomCardsLoading;

  const integrationsOverviewCards = useMemo(() => {
    return [...customCards, ...partnerWidgets]
      .filter((card) =>
        typeFilter && typeFilter.length > 0
          ? typeFilter.some((filter) => filter.id === card.integrationId)
          : true,
      )
      .filter(({ label }) =>
        searchTerm ? label.toLowerCase().includes(searchTerm) : true,
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customCards, partnerWidgets, typeFilter, searchTerm]);

  return (
    <GraphQLClientProvider
      configuration={{ baseUrl: config.url.partnerIntegrations }}
    >
      <Page.Section
        title="All integrations"
        titleMeta={
          customCards.length > 0 && (
            <Badge size="small" variant="secondary">
              {integrationsOverviewCards.length}
            </Badge>
          )
        }
        controls={
          <>
            <SearchInput
              onToggle={(isOpen) => {
                if (!isOpen) {
                  setSearchTerm(undefined);
                }
              }}
              onChange={(value) => setSearchTerm(value)}
              onSubmit={() => undefined}
            />

            <InputSelectionList
              clearable
              multiple
              className={styles.inputSelect}
              value={typeFilter ? typeFilter : []}
              onChange={(event) => setTypeFilter(event.selectedValues)}
              getItemTitleByValue={(value) => (
                <Avatar
                  icon={value?.icon}
                  color={value?.iconColors}
                  size="24"
                />
              )}
              placeholder={'Filter by integration'}
              dropdownProps={{ fixed: true, className: styles.dropdown }}
            >
              {Object.values(integrations).map((integration) => {
                const { id, label, icon, iconColors } = integration;
                return (
                  <SelectionList.Item key={id} value={integration}>
                    <FlexBox alignItems="center" gap="100">
                      <Avatar icon={icon} color={iconColors} size="24" />
                      {label}
                    </FlexBox>
                  </SelectionList.Item>
                );
              })}
            </InputSelectionList>
          </>
        }
      >
        {isLoading && <Spinner />}

        <Box as="div" className={styles.integrationsCardGrid}>
          {!isLoading &&
            integrationsOverviewCards.map((card) => (
              <IntegrationsCard key={card.id} integrationsCard={card} />
            ))}
        </Box>

        {!isLoading &&
          integrationsOverviewCards.length === 0 &&
          (typeFilter || searchTerm) && (
            <FlexBox justifyContent="center" width="100%" margin="400">
              <EmptyState
                title="No integration cards available"
                description="Seems like there are no matching cards for the specified filtering options."
              />
            </FlexBox>
          )}
      </Page.Section>
    </GraphQLClientProvider>
  );
};
